import { changeBaseItemIDToVariantID } from 'components/Sell/utils/common'
import Decimal from 'decimal.js'
import { keys, values } from 'lodash-es'
import { ItemType } from 'models'
import {
    ComboItemModifierOptionViewModel,
    ComboViewModel,
    EntityModifiersMap,
    OrderItemViewModel,
    PriceListData,
    UsedModifiers,
    UsedVariants,
} from 'store/models/sellModels'
import {
    getComboChoiceItemModifierOptionPrice,
    getItemPrice,
    getModifierOptionPrice,
} from 'utilities/price/getPrice'
import { round } from 'views/Sale/utils/orderCalculation/common'

class RecalculateOrder {
    orderItemView: OrderItemViewModel
    menuId: string
    priceListData: PriceListData
    comboChoicesMap: Record<string, ComboViewModel[]>
    entityModifiersMap: EntityModifiersMap
    comboModifierOptions: ComboItemModifierOptionViewModel[]

    constructor(
        orderItemView: OrderItemViewModel,
        menuId: string,
        priceListData: PriceListData,
        comboChoicesMap: Record<string, ComboViewModel[]>,
        entityModifiersMap: EntityModifiersMap,
        comboModifierOptions: ComboItemModifierOptionViewModel[],
    ) {
        this.orderItemView = orderItemView
        this.menuId = menuId
        this.priceListData = priceListData
        this.comboChoicesMap = comboChoicesMap
        this.entityModifiersMap = entityModifiersMap
        this.comboModifierOptions = comboModifierOptions
    }

    process() {
        this.recalculateItemAndVariant()

        if (this.orderItemView.usedModifiers)
            this.recalculateModifierOption(this.orderItemView.usedModifiers)

        this.recalculateComboChoices()

        this.resetPriceChangedError()

        this.orderItemView.isItemUnavailable =
            values(this.orderItemView.error).flatMap((i) => i).length > 0

        //recalculate subtotal
        let subTotal = new Decimal(this.orderItemView.lineTotal)
        if (this.orderItemView.usedItems) {
            keys(this.orderItemView.usedItems).forEach((bindId) => {
                const usedItem = this.orderItemView.usedItems[bindId]
                subTotal = subTotal.plus(usedItem.addPrice)
            })
        }
        this.orderItemView.subtotal = subTotal.mul(this.orderItemView.amount)
        this.orderItemView.lineTotal = subTotal
            .mul(this.orderItemView.amount)
            .toNumber()
    }

    getOrderItemView() {
        return this.orderItemView
    }

    private recalculateItemAndVariant() {
        let itemId = changeBaseItemIDToVariantID(this.orderItemView).item.itemId

        this.orderItemView.menuId = this.menuId
        this.orderItemView.lineTotal = getItemPrice(
            itemId,
            this.menuId,
            this.priceListData,
        )
    }

    private recalculateModifierOption(
        usedModifiers: UsedModifiers,
        isComboItem?: boolean,
    ) {
        let modLineTotal = new Decimal(0)
        keys(usedModifiers).forEach((bindId) => {
            const modOptions = usedModifiers[bindId]
            const hasAdditionalPrice =
                this.entityModifiersMap[bindId]?.hasAssociatedCost ?? true
            modOptions.forEach((modOption) => {
                const value = isComboItem
                    ? modOption.addPrice
                    : hasAdditionalPrice
                    ? getModifierOptionPrice(
                          modOption.modifierOptionId!,
                          this.menuId,
                          this.priceListData,
                      )
                    : 0
                modLineTotal = modLineTotal.plus(value)
                keys(modOption.subModifierOptions).forEach((bindId) => {
                    const subModifierOptions =
                        modOption.subModifierOptions[bindId]
                    const hasAdditionalPrice =
                        this.entityModifiersMap[bindId].hasAssociatedCost ??
                        true

                    subModifierOptions.forEach((subMod) => {
                        const subPrice = hasAdditionalPrice
                            ? getModifierOptionPrice(
                                  subMod.modifierOptionId,
                                  this.menuId,
                                  this.priceListData,
                              )
                            : 0
                        subMod.addPrice = subPrice
                        modLineTotal = modLineTotal.plus(subPrice)
                    })
                })
            })
        })

        this.orderItemView.lineTotal = round(
            new Decimal(this.orderItemView.lineTotal).plus(modLineTotal),
        ).toNumber()
    }

    private recalculateComboChoices() {
        const isComboItem = this.orderItemView.item.type === ItemType.Combo
        keys(this.orderItemView.usedItems).forEach((pathItem) => {
            let item = this.orderItemView.usedItems[pathItem]
            const itemId = this.orderItemView.item.itemId
            const selectedComboChoiceMap = this.comboChoicesMap[itemId]
            const selectedComboChoice = selectedComboChoiceMap.find(
                (f) => f.comboChoiceID === item.comboChoiceId,
            )
            const selectedComboItem = selectedComboChoice?.comboItems.find(
                (f) => f.comboChoiceID === item.comboChoiceId,
            )

            this.orderItemView.usedItems[pathItem].addPrice = getItemPrice(
                selectedComboItem?.comboChoiceItemId ?? '',
                this.menuId,
                this.priceListData,
            )

            if (item.usedModifiers) {
                //update additional price in modifier
                keys(item.usedModifiers).forEach((bindingModifierId) => {
                    const useModifier = item.usedModifiers[bindingModifierId]
                    useModifier.forEach((e) => {
                        e.addPrice = getComboChoiceItemModifierOptionPrice(
                            item.comboChoiceId,
                            bindingModifierId,
                            e.modifierOptionId,
                            values(item.usedVariants)?.sort()?.join(',') ?? '',
                            this.comboModifierOptions,
                            this.orderItemView.menuId ?? '',
                            this.priceListData,
                        )
                    })
                })
                this.recalculateModifierOption(item.usedModifiers, isComboItem)
            }

            if (item.usedVariants)
                this.recalculateComboChoice(
                    item.comboChoiceId,
                    item.usedVariants,
                )
        })
    }

    private recalculateComboChoice(
        comboChoiceID: string,
        usedVariants: UsedVariants,
    ) {
        let comboChoices = this.comboChoicesMap[
            this.orderItemView.item.itemId
        ].find((i) => i.comboChoiceID === comboChoiceID)

        let comboChoiceOptions =
            comboChoices?.comboItems.flatMap((combo) =>
                combo.variantGroups.flatMap((group) =>
                    group.variantOptions.map((option) => ({
                        variantOptionId: option.variantOptionID,
                        addPrice: option.addPrice,
                    })),
                ),
            ) ?? []

        values(usedVariants).forEach((variantOptionId) => {
            this.orderItemView.lineTotal = round(
                new Decimal(this.orderItemView.lineTotal).plus(
                    comboChoiceOptions.find(
                        (i) => i.variantOptionId === variantOptionId,
                    )?.addPrice ?? 0,
                ),
            ).toNumber()
        })
    }

    private resetPriceChangedError() {
        this.orderItemView.error = {
            'item-unavailable':
                this.orderItemView.error?.['item-unavailable'] ?? [],
            'price-changed': [],
            'time-unavailable':
                this.orderItemView.error?.['time-unavailable'] ?? [],
            'invalid-modifier-option':
                this.orderItemView.error?.['invalid-modifier-option'] ?? [],
        }
    }
}
export default RecalculateOrder
